import { useEffect, useRef } from "react";
import AdminImageFallback from "../components/AdminImageFallback";
import {
  Eye,
  EyeOff,
  MoreHorizontal,
  Pencil,
  Star,
  Trash2,
} from "lucide-react";

function formatPrice(value) {
  const price = Number(value);

  if (!Number.isFinite(price)) {
    return "No price";
  }

  return `$${price.toFixed(2)}`;
}

function MenuItemCard({
  item,
  isMenuOpen,
  onToggleMenu,
  onCloseMenu,
  onEdit,
  onDelete,
  onToggleActive,
  onToggleAvailable,
  onToggleFeatured,
}) {
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    function handlePointerDown(event) {
      if (!menuRef.current?.contains(event.target)) {
        onCloseMenu();
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onCloseMenu();
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuOpen, onCloseMenu]);

  function runMenuAction(action) {
    onCloseMenu();
    action(item);
  }

  const activeVariants = Array.isArray(item.variants)
    ? [...item.variants]
        .filter((variant) => variant.is_active)
        .sort(
          (a, b) =>
            Number(a.display_order || 0) -
            Number(b.display_order || 0),
        )
    : [];

  const activeFlavors = Array.isArray(item.flavors)
    ? item.flavors.filter((flavor) => flavor.is_active)
    : [];

  const availableVariants = activeVariants.filter(
    (variant) => variant.is_available,
  );

  const addOnCount = Array.isArray(item.add_ons)
    ? item.add_ons.filter((addOn) => addOn.is_active).length
    : 0;

  const flavorCount = Array.isArray(item.flavors)
    ? activeFlavors.length
    : Number(item.flavor_count || 0);

  return (
    <article
      className={[
        "admin-menu-card",
        !item.is_active && "admin-menu-card--inactive",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="admin-menu-card__image">
        <AdminImageFallback
          src={item.image_url}
          alt={item.name}
          className="admin-menu-card__media-image"
          uploadedImageClassName="admin-menu-card__media-image--uploaded"
          fallbackClassName="admin-menu-card__media-image--fallback"
        />

        <div className="admin-menu-card__badges">
          {item.is_featured && (
            <span className="admin-menu-badge admin-menu-badge--featured">
              <Star size={13} />
              Featured
            </span>
          )}

          {item.is_seasonal && (
            <span className="admin-menu-badge admin-menu-badge--seasonal">
              Seasonal
            </span>
          )}

          {!item.is_available && (
            <span className="admin-menu-badge admin-menu-badge--unavailable">
              Unavailable
            </span>
          )}

          {!item.is_active && (
            <span className="admin-menu-badge">
              Hidden
            </span>
          )}
        </div>
      </div>

      <div className="admin-menu-card__body">
        <div className="admin-menu-card__top">
          <div>
            <span className="admin-menu-card__category">
              {item.category_name || "Uncategorized"}
            </span>

            <h3>{item.name}</h3>
          </div>

          <div
            className="admin-menu-card__more-wrapper"
            ref={menuRef}
          >
            <button
              type="button"
              className="admin-icon-button"
              aria-label={`More actions for ${item.name}`}
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
              onClick={(event) => {
                event.stopPropagation();
                onToggleMenu();
              }}
            >
              <MoreHorizontal size={19} />
            </button>

            {isMenuOpen && (
              <div
                className="admin-menu-card__dropdown"
                role="menu"
                aria-label={`Actions for ${item.name}`}
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => runMenuAction(onEdit)}
                >
                  <Pencil size={15} />
                  Edit item
                </button>

                <button
                  type="button"
                  role="menuitem"
                  onClick={() => runMenuAction(onToggleActive)}
                >
                  {item.is_active ? (
                    <EyeOff size={15} />
                  ) : (
                    <Eye size={15} />
                  )}
                  {item.is_active ? "Hide item" : "Show item"}
                </button>

                <button
                  type="button"
                  role="menuitem"
                  onClick={() => runMenuAction(onToggleAvailable)}
                >
                  <Eye size={15} />
                  {item.is_available
                    ? "Mark unavailable"
                    : "Mark available"}
                </button>

                <button
                  type="button"
                  role="menuitem"
                  onClick={() => runMenuAction(onToggleFeatured)}
                >
                  <Star size={15} />
                  {item.is_featured
                    ? "Remove from featured"
                    : "Feature item"}
                </button>

                <button
                  type="button"
                  role="menuitem"
                  className="admin-menu-card__dropdown-danger"
                  onClick={() => runMenuAction(onDelete)}
                >
                  <Trash2 size={15} />
                  Delete item
                </button>
              </div>
            )}
          </div>
        </div>

        <p>
          {item.short_description ||
            item.description ||
            "No description has been added."}
        </p>

        <div className="admin-menu-card__summary">
          <span>
            <strong>{activeVariants.length}</strong>{" "}
            {activeVariants.length === 1
              ? "price option"
              : "price options"}
          </span>

          <span>
            <strong>{flavorCount}</strong>{" "}
            {flavorCount === 1 ? "flavor" : "flavors"}
          </span>

          {addOnCount > 0 && (
            <span>
              <strong>{addOnCount}</strong>{" "}
              {addOnCount === 1 ? "add-on" : "add-ons"}
            </span>
          )}
        </div>


        <div className="admin-menu-card__pricing">
          {activeVariants.length ? (
            activeVariants.map((variant) => (
              <div
                key={variant.id || variant.name}
                className={[
                  "admin-menu-card__price-row",
                  !variant.is_available &&
                    "admin-menu-card__price-row--unavailable",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <div>
                  <span>{variant.name}</span>

                  {variant.quantity > 1 && (
                    <small>
                      Includes {variant.quantity}
                    </small>
                  )}
                </div>

                <strong>
                  {formatPrice(variant.price)}
                </strong>
              </div>
            ))
          ) : (
            <span className="admin-menu-card__no-price">
              No prices have been added.
            </span>
          )}
        </div>

        <div className="admin-menu-card__bottom">
          <button
            type="button"
            className={[
              "admin-menu-availability",
              item.is_available
                ? "admin-menu-availability--available"
                : "admin-menu-availability--unavailable",
            ].join(" ")}
            onClick={() => onToggleAvailable(item)}
          >
            {item.is_available
              ? `${availableVariants.length} available`
              : "Unavailable"}
          </button>

          <div className="admin-menu-card__actions">
            <button
              type="button"
              className="admin-icon-button"
              title="Edit item"
              onClick={() => onEdit(item)}
            >
              <Pencil size={17} />
            </button>

            <button
              type="button"
              className="admin-icon-button admin-icon-button--danger"
              title="Delete item"
              onClick={() => onDelete(item)}
            >
              <Trash2 size={17} />
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

export default MenuItemCard;
